import { computed, inject, Injectable, Signal } from '@angular/core';
import { Theme } from '@core/enums/theme';
import { HttpClient } from '@angular/common/http';
import { concat, map, Observable, of, switchMap, take, toArray } from 'rxjs';
import { ThemeService } from '@core/services/theme.service';
import {
  ApiComparisonFilterOperator,
  COREZILLA_API_CONFIG,
  CoreBaseEntityApiService,
  CorezillaApiConfig,
  SkipInterceptHeaders,
} from '@corezilla/api';
import { AssetResponse } from '@shared/api/asset/asset-response';
import { AssetUpload } from '@shared/api/asset/asset-upload';
import { Asset } from '@shared/api/asset/asset';
import { AssetEntityName } from '@shared/api/asset/asset-entity-name';
import { AssetMode } from '@shared/api/asset/asset-mode';
import { AssetOwner } from '@shared/api/asset/asset-owner';
import {
  StagedAsset,
  stagedFile,
  stagedModes,
} from '@shared/api/asset/staged-asset';

@Injectable()
export class AssetService {
  /** Theme-aware asset handling and uploads. */
  private readonly config: CorezillaApiConfig = inject(COREZILLA_API_CONFIG);

  private readonly http: HttpClient = inject(HttpClient);

  private readonly themeService: ThemeService = inject(ThemeService);

  /** Generic entity API used to query the assets list endpoint. */
  private readonly entityApi = inject(CoreBaseEntityApiService<Asset>);

  /**
   * PROPERTIES
   * **/

  /** Core API base for building asset URLs. */
  public readonly apiUrl = this.config.apiCoreUrl;

  /**
   * A media id holds at most one asset per colour mode plus one mode-less asset, so three rows
   * is the upper bound of a lookup by media id.
   **/
  private static readonly MaxAssetsPerMediaId = 3;

  /** Returns an asset URL or a theme-aware placeholder when none is available. */
  public getAssetUrlOrPlaceholder(
    item: AssetOwner | undefined,
    entityName: AssetEntityName = 'component'
  ): Signal<string> {
    return item?.asset
      ? this.getAssetUrl(item, entityName)
      : this.getPlaceholderImage();
  }

  /**
   * Every asset sharing one media id: the mode-less asset and the light and dark variant, as far
   * as they exist. The media id is unique per colour mode, not on its own, so a lookup can only
   * ever narrow it down to this set.
   */
  public findAllByMediaId(mediaId: string): Observable<Asset[]> {
    return this.entityApi
      .getAll('assets', {
        page: 1,
        pageSize: AssetService.MaxAssetsPerMediaId,
        filter: [
          {
            property: 'mediaId',
            operator: ApiComparisonFilterOperator.EQ,
            value: mediaId,
          },
        ],
      })
      .pipe(map(result => result.items));
  }

  /**
   * Uploads one asset file. Passing `entityId` lets the API assign the new asset to that entity
   * right away; leave it out when the caller persists the media id on the entity itself.
   */
  public upload(
    data: AssetUpload,
    entityId?: number
  ): Observable<AssetResponse> {
    const res = `${this.apiUrl}/assets`;

    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('mediaId', data.mediaId);
    formData.append('entityName', data.entityName);

    if (entityId !== undefined) {
      formData.append('entityId', entityId.toString());
    }

    if (data.mode?.length) {
      formData.append('mode', data.mode);
    }

    return this.http
      .post(res, formData, {
        headers: SkipInterceptHeaders,
      })
      .pipe(map(obj => AssetResponse.from(obj)));
  }

  /**
   * Replaces the image of an existing asset, keeping its media id and colour mode. Sent as a
   * POST because a file can only be delivered as multipart, which PHP parses for POST alone.
   */
  public replace(assetId: number, file: File): Observable<AssetResponse> {
    const res = `${this.apiUrl}/assets/${assetId}/file`;

    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post(res, formData, {
        headers: SkipInterceptHeaders,
      })
      .pipe(map(obj => AssetResponse.from(obj)));
  }

  /**
   * Sends the staged images of one media id: a colour mode the media id does not hold yet is
   * created, one that is already taken has its image replaced. Both colour modes are handled
   * independently, so a light image can be replaced without touching the dark one.
   *
   * The existing assets are resolved here rather than handed in, so create-or-replace is decided
   * against the state at save time — not against what a form resolved minutes earlier.
   *
   * Sequential rather than parallel: the variants of a media id share a cache entry upstream.
   */
  public saveStagedAsset(
    staged: StagedAsset,
    entityName: AssetEntityName,
    entityId?: number
  ): Observable<AssetResponse[]> {
    const modes = stagedModes(staged);
    if (!modes.length) return of([]);

    return this.findAllByMediaId(staged.mediaId).pipe(
      take(1),
      switchMap(rows =>
        concat(
          ...modes.map(mode =>
            this.saveStagedMode(staged, mode, rows, entityName, entityId)
          )
        ).pipe(toArray())
      )
    );
  }

  /** Creates or replaces the image of a single colour mode. */
  private saveStagedMode(
    staged: StagedAsset,
    mode: AssetMode,
    rows: Asset[],
    entityName: AssetEntityName,
    entityId?: number
  ): Observable<AssetResponse> {
    const file = stagedFile(staged, mode)!;

    // Only an asset stored for exactly this mode is replaced. A mode-less asset stays as it is:
    // it serves every mode, so replacing it would change the mode the user did not touch.
    const existing = rows.find(row => row.mode === mode);

    return existing
      ? this.replace(existing.id, file)
      : this.upload(
          { file, mediaId: staged.mediaId, entityName, mode },
          entityId
        );
  }

  /** Builds a reactive asset URL bound to the current theme. */
  private getAssetUrl(item: AssetOwner, entityName: AssetEntityName) {
    return computed(() => {
      const dark = this.themeService.theme() === Theme.DARK;
      const url = dark ? item.darkAssetUrl : item.lightAssetUrl;

      return url
        ? `${this.apiUrl}${url}`
        : this.buildShowUrl(item.asset!, entityName, dark ? 'dark' : 'light');
    });
  }

  /** Provides a light/dark placeholder image based on the active theme. */
  private getPlaceholderImage() {
    return computed(() => {
      const theme = this.themeService.theme();

      return theme === Theme.DARK
        ? `assets/images/icon-light.svg`
        : 'assets/images/icon.svg';
    });
  }

  /**
   * Builds the server url of one colour-mode variant. Only used as a fallback: entities that
   * expose ready-made light and dark urls are served those instead.
   **/
  private buildShowUrl(
    mediaId: string,
    entityName: AssetEntityName,
    mode: AssetMode
  ): string {
    return `${this.apiUrl}/assets/show/${entityName}/${mediaId}/${mode}`;
  }
}
